import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroArrowDownTray, heroDocument, heroXMark } from '@ng-icons/heroicons/outline';
import { FilesApiService } from '../../../../core/api/files-api.service';
import { contentTypeToMediaKind, formatBytes, type MediaKind } from '../../media/media-kind';
import { UiBackdropButton, UiBadge, UiIconButton, UiMediaButton, UiPanelCard, UiSkeletonRow } from '../../../../shared/ui';
import type { ThreadAttachment } from '../../models/conversation-view.types';
import { AudioPlayerComponent } from './audio-player';
import { AudioTranscriptionComponent } from './audio-transcription';

interface AnalysisBadgeViewModel {
  label: string;
  tone: 'info' | 'warning' | 'danger';
  /** Present only for `skipped_insufficient_credit` — jumps straight to Cobrança. */
  href: string | null;
}

interface AttachmentViewModel {
  id: string;
  fileId: string;
  kind: MediaKind;
  filename: string;
  size: string;
  /** LQIP data URI painted (blurred) behind the full image, or null → skeleton fallback. */
  placeholder: string | null;
  /** CSS `aspect-ratio` value (e.g. `"400 / 300"`) reserving layout space, or null when unknown. */
  aspectRatio: string | null;
  /** null = nothing to show (succeeded / unsupported / no analyzer ran) — no badge rendered. */
  analysisBadge: AnalysisBadgeViewModel | null;
}

/** `succeeded`/`unsupported`/null render no badge — only a state the user should actually notice
 *  (still working, or genuinely didn't run) gets one. Mirrors the priority already used to reduce
 *  `analysisArtifacts` server-side (attachment-block.formatter.ts / conversation.service.ts). */
function toAnalysisBadge(status: ThreadAttachment['analysisStatus']): AnalysisBadgeViewModel | null {
  switch (status) {
    case 'pending':
      return { label: 'Analisando…', tone: 'info', href: null };
    case 'failed':
      return { label: 'Falha ao analisar', tone: 'danger', href: null };
    case 'skipped_insufficient_credit':
      return { label: 'Sem crédito p/ analisar', tone: 'warning', href: '/configuracoes/cobranca' };
    default:
      return null;
  }
}

/** Renders a message's attachments by kind: image (with lightbox), video, audio, document card. */
@Component({
  selector: 'app-message-attachments',
  imports: [NgIcon, RouterLink, UiBackdropButton, UiBadge, UiIconButton, UiMediaButton, UiPanelCard, UiSkeletonRow, AudioPlayerComponent, AudioTranscriptionComponent],
  providers: [provideIcons({ heroArrowDownTray, heroDocument, heroXMark })],
  templateUrl: './message-attachments.html',
  styleUrl: './message-attachments.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MessageAttachmentsComponent {
  private readonly filesApi = inject(FilesApiService);
  private readonly urls = signal<Record<string, string>>({});
  // fileIds already requested, so re-renders never re-fire a read-url fetch.
  private readonly requested = new Set<string>();

  readonly attachments = input.required<readonly ThreadAttachment[]>();
  readonly lightboxUrl = signal<string | null>(null);
  /** fileIds whose full-res <img> has fired `load` — drives the fade-in + hides the spinner. */
  private readonly loadedFileIds = signal<ReadonlySet<string>>(new Set());

  /** fileId → signedUrl already delivered in the message payload (0 round-trips). */
  private readonly payloadUrls = computed<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    for (const attachment of this.attachments()) {
      if (attachment.signedUrl) map[attachment.fileId] = attachment.signedUrl;
    }
    return map;
  });

  readonly items = computed<AttachmentViewModel[]>(() =>
    this.attachments().map((attachment) => ({
      id: attachment.id,
      fileId: attachment.fileId,
      // A sticker is image/webp same as a regular photo — the provider marker overrides the
      // mime-based classification so it renders small and borderless, not a framed photo.
      kind: attachment.isSticker ? 'sticker' : contentTypeToMediaKind(attachment.contentType),
      filename: attachment.filename ?? 'arquivo',
      size: formatBytes(attachment.sizeBytes),
      placeholder: attachment.placeholder ?? null,
      aspectRatio: attachment.width && attachment.height ? `${attachment.width} / ${attachment.height}` : null,
      analysisBadge: toAnalysisBadge(attachment.analysisStatus),
    })),
  );

  constructor() {
    // The signed URL now arrives in the message payload, so no per-image round-trip is needed.
    // Only fall back to resolving on demand for attachments WITHOUT one (e.g. older cached
    // messages mapped before the field existed). Never write a signal from a template getter.
    effect(() => {
      for (const attachment of this.attachments()) {
        if (!attachment.signedUrl) {
          this.resolve(attachment.fileId);
        }
      }
    });
  }

  /** Pure template accessor: payload signedUrl first, on-demand fallback second, null while loading. */
  urlFor(fileId: string): string | null {
    return this.payloadUrls()[fileId] || this.urls()[fileId] || null;
  }

  openLightbox(fileId: string): void {
    const url = this.urlFor(fileId);
    if (url) {
      this.lightboxUrl.set(url);
    }
  }

  closeLightbox(): void {
    this.lightboxUrl.set(null);
  }

  /** True once the full-res image has loaded — template fades it in and hides the spinner. */
  isLoaded(fileId: string): boolean {
    return this.loadedFileIds().has(fileId);
  }

  onImageLoad(fileId: string): void {
    if (this.loadedFileIds().has(fileId)) return;
    this.loadedFileIds.update((current) => new Set(current).add(fileId));
  }

  private resolve(fileId: string): void {
    if (this.requested.has(fileId)) {
      return;
    }
    this.requested.add(fileId);
    this.filesApi.getReadUrl(fileId).subscribe({
      next: (url) => this.urls.update((current) => ({ ...current, [fileId]: url })),
      error: () => this.requested.delete(fileId),
    });
  }
}
